import { Box, Table } from '@chakra-ui/react'
import React from 'react'

import { Checkbox, SortableColumnHeader } from '@mangovoice/mango-ui'
import type { ExtensionInstance } from '@mangovoice/types'

import { useExtensionsI18n } from '../i18n/provider'
import type {
  ExtensionForDisplay,
  LineForDisplay,
} from '../types/extension.types'
import { ExtensionRow, LineRow, OtherExtensionRow } from './ExtensionRow'

interface OtherExtensionsTableProps {
  extensions: ExtensionInstance[]
  pbxId: string
  canEditExtension: (ext: ExtensionForDisplay) => boolean
  canDeleteExtension: (ext: ExtensionForDisplay) => boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  ordering: string
  onOrderingChange: (value: string) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
}

export const OtherExtensionsTable: React.FC<OtherExtensionsTableProps> = ({
  extensions,
  pbxId,
  canEditExtension,
  canDeleteExtension,
  onDeleteClick,
  ordering,
  onOrderingChange,
  getEditRoute,
}) => {
  const { text } = useExtensionsI18n()
  const showActions = extensions.some((ext) =>
    canDeleteExtension(ext as ExtensionForDisplay),
  )
  return (
    <Box overflowX="auto">
      <Table.Root variant="outline">
        <Table.Header>
          <Table.Row>
            <SortableColumnHeader
              label={text('extensions.columns.number', 'Number')}
              field="number"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.name', 'Name')}
              field="name"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.created', 'Created')}
              field="created_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.updated', 'Updated')}
              field="updated_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            {showActions && (
              <Table.ColumnHeader px={4} py={3}>
                {text('extensions.columns.actions', 'Actions')}
              </Table.ColumnHeader>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {extensions.map((ext) => (
            <OtherExtensionRow
              key={ext.id}
              extension={ext as ExtensionForDisplay}
              pbxId={pbxId}
              canEdit={canEditExtension(ext as ExtensionForDisplay)}
              canDelete={canDeleteExtension(ext as ExtensionForDisplay)}
              onDeleteClick={onDeleteClick}
              showActions={showActions}
              getEditRoute={getEditRoute}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

interface ExtensionsTableProps {
  extensions: ExtensionInstance[]
  pbxId: string
  canEditExtension: (ext: ExtensionForDisplay) => boolean
  canDeleteExtension: (ext: ExtensionForDisplay) => boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  ordering: string
  onOrderingChange: (value: string) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
}

export const ExtensionsTable: React.FC<ExtensionsTableProps> = ({
  extensions,
  pbxId,
  canEditExtension,
  canDeleteExtension,
  onDeleteClick,
  ordering,
  onOrderingChange,
  getEditRoute,
}) => {
  const { text } = useExtensionsI18n()
  return (
    <Box overflowX="auto">
      <Table.Root variant="outline">
        <Table.Header>
          <Table.Row>
            <SortableColumnHeader
              label={text('extensions.columns.number', 'Number')}
              field="number"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.name', 'Name')}
              field="name"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <Table.ColumnHeader px={4} py={3}>
              {text('extensions.columns.type', 'Type')}
            </Table.ColumnHeader>
            <SortableColumnHeader
              label={text('extensions.columns.lastUpdated', 'Last updated')}
              field="updated_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.created', 'Created')}
              field="created_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <Table.ColumnHeader px={4} py={3}>
              {text('extensions.columns.actions', 'Actions')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {extensions.map((ext) => (
            <ExtensionRow
              key={ext.id}
              extension={ext as ExtensionForDisplay}
              pbxId={pbxId}
              canEdit={canEditExtension(ext as ExtensionForDisplay)}
              canDelete={canDeleteExtension(ext as ExtensionForDisplay)}
              onDeleteClick={onDeleteClick}
              getEditRoute={getEditRoute}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

interface LinesTableProps {
  lines: LineForDisplay[]
  pbxId: string
  canEditExtension: (ext: ExtensionForDisplay) => boolean
  canDeleteExtension: (ext: ExtensionForDisplay) => boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  onToggleActiveClick?: (line: LineForDisplay) => void
  ordering: string
  onOrderingChange: (value: string) => void
  selectedLineIds?: Set<number>
  onToggleLine?: (lineId: number) => void
  onSelectAll?: (checked: boolean) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
  grafanaBaseUrl?: string
}

export const LinesTable: React.FC<LinesTableProps> = ({
  lines,
  pbxId,
  canEditExtension,
  canDeleteExtension,
  onDeleteClick,
  onToggleActiveClick,
  ordering,
  onOrderingChange,
  selectedLineIds,
  onToggleLine,
  onSelectAll,
  getEditRoute,
  grafanaBaseUrl,
}) => {
  const { text } = useExtensionsI18n()
  const showSelection =
    selectedLineIds != null && onToggleLine != null && onSelectAll != null
  const allSelected =
    showSelection &&
    lines.length > 0 &&
    lines.every((l) => selectedLineIds!.has(l.id))
  const someSelected =
    showSelection &&
    lines.length > 0 &&
    lines.some((l) => selectedLineIds!.has(l.id))

  return (
    <Box overflowX="auto">
      <Table.Root variant="outline">
        <Table.Header>
          <Table.Row>
            {showSelection && (
              <Table.ColumnHeader px={4} py={3} w="40px">
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? 'indeterminate' : false
                  }
                  onCheckedChange={(e) => onSelectAll(Boolean(e.checked))}
                  inputProps={{
                    'aria-label': text(
                      'extensions.row.selectAllLines',
                      'Select all lines',
                    ),
                  }}
                />
              </Table.ColumnHeader>
            )}
            <SortableColumnHeader
              label={text('extensions.columns.number', 'Number')}
              field="number"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.name', 'Name')}
              field="name"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text(
                'extensions.columns.assignedProfile',
                'Assigned profile',
              )}
              field="assigned_profile__name"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.location', 'Location')}
              field="location__name"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.created', 'Created')}
              field="created_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <SortableColumnHeader
              label={text('extensions.columns.updated', 'Updated')}
              field="updated_at"
              ordering={ordering}
              onOrderingChange={onOrderingChange}
            />
            <Table.ColumnHeader px={4} py={3}>
              {text('extensions.columns.actions', 'Actions')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {lines.map((line) => (
            <LineRow
              key={line.id}
              line={line}
              pbxId={pbxId}
              canEdit={canEditExtension(line)}
              canDelete={canDeleteExtension(line)}
              onDeleteClick={onDeleteClick}
              onToggleActiveClick={onToggleActiveClick}
              isSelected={
                showSelection ? selectedLineIds!.has(line.id) : undefined
              }
              onToggleSelect={showSelection ? onToggleLine : undefined}
              getEditRoute={getEditRoute}
              grafanaBaseUrl={grafanaBaseUrl}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
